import { looseUnitOf, packsPerUnit, type CustomerSummary, type Item, type OrderCreate } from '@morales/shared';

export type Tier = CustomerSummary['priceTier'];
export type Channel = OrderCreate['channel'];

/**
 * One line of an order being written. Quantity is counted in packs (the item's packUnit):
 * the stepper moves whole packs and the split toggle adds half a pack (qty 0.5).
 */
export interface DraftLine {
  key: string;
  item: Item;
  packs: number;
  split: boolean;
  /** sold out of the box (decision 166): how many pounds or each, typed; null = by the box */
  loose?: number | null;
  /** an order being changed (C6): the order's own line this is, while its product and unit stay */
  lineNo?: number;
  /** the unit the saved line was in: another unit is a new line (the old one comes off) */
  savedUnit?: string;
  /** what the saved line held, to the last decimal; sent back as it is until a person touches the line */
  savedQty?: number;
  touched?: boolean;
  /** a saved line the screen cannot change (its product archived): shown, sent back as it was */
  fixed?: boolean;
  /** the line's price: a rate the owner or the office typed (rateTyped), or a saved line's price, kept as it is */
  rate?: number | null;
  rateTyped?: boolean;
}

/** The unit a product is sold in out of its box (lb, each, bunch), or null. */
export const looseUnit = (item: Item) => looseUnitOf(item);
export const lineQty = (line: DraftLine): number => (line.loose != null ? line.loose : line.packs + (line.split ? 0.5 : 0));
export const lineUnit = (line: DraftLine): string =>
  line.loose != null ? (looseUnit(line.item) ?? line.savedUnit ?? line.item.packUnit) : unitIsPack(line.savedUnit, line.item.packUnit) && line.savedUnit ? line.savedUnit : line.item.packUnit;

/** By the box or by the pound/each: the same amount in the other unit (1 case of 25 lb is 25 lb, 75 lb is 3 cases).
 * Another unit is another price: a rate typed for the box is never kept for a pound (review of decision 178). */
export function toggleLoose(line: DraftLine): DraftLine {
  const unit = looseUnit(line.item);
  if (!unit) return line;
  const per = packsPerUnit(unit, line.item.content, line.item.contentUnit);
  if (line.loose != null) {
    // back to whole and half boxes, never under one
    const halves = per ? Math.max(2, Math.round(line.loose * per * 2)) : line.packs * 2 + (line.split ? 1 : 0);
    const split = halves % 2 === 1 && line.item.sellSplit;
    return { ...line, loose: null, packs: split ? (halves - 1) / 2 : Math.max(1, Math.round(halves / 2)), split, rate: null, rateTyped: false };
  }
  // never a blank the line saves without (end-user test)
  return { ...line, loose: per ? Math.round((lineQty(line) / per) * 100) / 100 : 0, split: false, rate: null, rateTyped: false };
}

/** Customer's tier price, falling back to the standard price. */
export function unitPrice(item: Item, tier: Tier): number | null {
  return item.prices[tier] ?? item.prices.standard ?? null;
}

/** Line total in whole cents, rounded half-up the way the database rounds numeric. */
export function lineCents(line: DraftLine, tier: Tier): number | null {
  // a pound or each is priced by the engine when the order is saved (its share of the box price, decision 166)
  if (line.loose != null) return null;
  const price = unitPrice(line.item, tier);
  if (price == null) return null;
  const halves = line.packs * 2 + (line.split ? 1 : 0);
  return Math.round((halves * Math.round(price * 100)) / 2);
}

/** Running total in dollars; lines with no price on file count as zero. */
export function orderTotal(lines: readonly DraftLine[], tier: Tier): number {
  return lines.reduce((sum, line) => sum + (lineCents(line, tier) ?? 0), 0) / 100;
}

export function newLine(item: Item, key: string): DraftLine {
  return { key, item, packs: 1, split: false };
}

/** Adding an item already on the order bumps that line instead of duplicating it. */
export function addItem(lines: readonly DraftLine[], item: Item, key: string): DraftLine[] {
  // the line the screen can change (not one kept as it is), bumped and marked touched so the change is sent
  const existing = lines.find((l) => l.item.id === item.id && !l.fixed);
  if (existing) return lines.map((l) => (l === existing ? { ...stepPacks(l, 1), touched: true } : l));
  return [...lines, newLine(item, key)];
}

/** Whole packs never go below 1, or below 0 when the line carries a split (so qty stays >= 0.5). */
export function stepPacks(line: DraftLine, delta: number): DraftLine {
  const min = line.split ? 0 : 1;
  return { ...line, packs: Math.max(min, line.packs + delta) };
}

export function canStepDown(line: DraftLine): boolean {
  return line.packs > (line.split ? 0 : 1);
}

export function toggleSplit(line: DraftLine): DraftLine {
  if (!line.item.sellSplit) return line;
  return line.split ? { ...line, split: false, packs: Math.max(1, line.packs) } : { ...line, split: true };
}

export function buildOrder(input: {
  customerId: string;
  requestedDate: string;
  channel: Channel;
  notes: string;
  lines: readonly DraftLine[];
}): OrderCreate {
  const notes = input.notes.trim();
  return {
    customerId: input.customerId,
    requestedDate: input.requestedDate,
    channel: input.channel,
    promised: false,
    ...(notes ? { notes } : {}),
    lines: input.lines.map((l) => ({
      itemId: l.item.id, qty: lineQty(l), unit: lineUnit(l) as OrderCreate['lines'][number]['unit'],
      ...(l.rate != null && l.rateTyped ? { unitPrice: l.rate } : {}),
    })),
  };
}

/**
 * The lines as an order change sends them (C6). A saved line nobody touched goes back exactly as it was saved (its unit,
 * its quantity to the last decimal, its price), so opening an order never changes it; a touched one names its saved line
 * while its unit stays (changed in place) and keeps its price; one in another unit now is a new line, priced again
 * unless a rate is typed. A saved line with nothing on it (shorted to 0) is left out: the order keeps it as it is.
 */
export function revisedLines(lines: readonly DraftLine[]) {
  return lines.flatMap((l) => {
    if (l.lineNo !== undefined && !l.touched) {
      if (!(l.savedQty! > 0)) return [];
      return [{ itemId: l.item.id, qty: l.savedQty!, unit: l.savedUnit as OrderCreate['lines'][number]['unit'], lineNo: l.lineNo, ...(l.rate != null ? { unitPrice: l.rate } : {}) }];
    }
    const same = l.lineNo !== undefined && sameUnit(lineUnit(l), l.savedUnit, l.item.packUnit);
    return [{
      itemId: l.item.id, qty: lineQty(l), unit: (same ? l.savedUnit : lineUnit(l)) as OrderCreate['lines'][number]['unit'],
      ...(same ? { lineNo: l.lineNo } : {}),
      ...(l.rate != null && (l.rateTyped || same) ? { unitPrice: l.rate } : {}),
    }];
  });
}

/** Boxes, cases, sacks, bags, crates, bales and jars are all "the pack" (the server's own rule). */
const CONTAINERS = new Set(['BX', 'CS', 'SK', 'BG', 'CR', 'BL', 'JR']);
export const unitIsPack = (unit: string | null | undefined, pack: string | null | undefined): boolean =>
  !unit || unit === pack || (CONTAINERS.has(unit) && !!pack && CONTAINERS.has(pack));
const sameUnit = (now: string, saved: string | undefined, pack: string) => now === saved || (unitIsPack(now, pack) && unitIsPack(saved, pack));

/**
 * A saved order line as a line on the screen: boxes on the stepper (a whole or a half; 2.25 boxes as they are), pounds or
 * each typed. `fixed` when the screen cannot change it (its product archived): shown and sent back as it was.
 */
export function draftFromLine(l: { lineNo: number; qty: number; unit: string; unitPrice: number | null }, item: Item, key: string, fixed = false): DraftLine {
  const byBox = unitIsPack(l.unit, item.packUnit);
  const halves = l.qty * 2;
  const wholeOrHalf = Math.abs(halves - Math.round(halves)) < 1e-9;
  return {
    key, item, lineNo: l.lineNo, savedUnit: l.unit, savedQty: l.qty, touched: false, fixed,
    packs: byBox ? (wholeOrHalf ? Math.floor(l.qty) : l.qty) : 1,
    split: byBox && wholeOrHalf && Math.round(halves) % 2 === 1,
    loose: byBox ? null : l.qty,
    // its price stays as it is unless a person changes it (a price the engine set, or one typed then)
    rate: l.unitPrice,
  };
}

/** The customer's next delivery day after today (ISO weekdays, 1 = Monday), else tomorrow. */
export function nextDeliveryDay(today: string, days: readonly number[]): string {
  const at = (n: number) => new Date(Date.parse(`${today}T12:00:00Z`) + n * 86_400_000);
  for (let n = 1; n <= 7; n += 1) {
    const d = at(n);
    if (days.length === 0 || days.includes(((d.getUTCDay() + 6) % 7) + 1)) return d.toISOString().slice(0, 10);
  }
  return at(1).toISOString().slice(0, 10);
}
export const isoWeekday = (iso: string): number => ((new Date(`${iso}T12:00:00Z`).getUTCDay() + 6) % 7) + 1;
